import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertTriangle, Trash2, GitMerge } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import HeaderNav from '@/components/HeaderNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function DuplicateLeads() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [duplicateLeads, setDuplicateLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [targetLeadId, setTargetLeadId] = useState('');
  const [allLeads, setAllLeads] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const [duplicatesRes, leadsRes] = await Promise.all([
        axios.get(`${API}/leads?segment=duplicates`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/leads`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setDuplicateLeads(duplicatesRes.data);
      setAllLeads(leadsRes.data);
    } catch (error) {
      toast.error('Failed to load duplicate leads');
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = (lead) => {
    setSelectedLead(lead);
    setShowMergeDialog(true);
  };

  const confirmMerge = async () => {
    if (!targetLeadId) {
      toast.error('Please enter the target lead ID');
      return;
    }

    const token = localStorage.getItem('token');
    try {
      // Find target lead
      const targetLead = allLeads.find(l => l.lead_number === targetLeadId || l.id === targetLeadId);
      if (!targetLead) {
        toast.error('Target lead not found');
        return;
      }

      // Merge projects from duplicate into target
      const mergedProjects = [...new Set([...targetLead.projects, ...selectedLead.projects])];
      
      await axios.put(`${API}/leads/${targetLead.id}`, 
        { projects: mergedProjects },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Delete the duplicate lead
      await axios.delete(`${API}/leads/${selectedLead.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Leads merged successfully');
      setShowMergeDialog(false);
      setTargetLeadId('');
      loadData();
    } catch (error) {
      toast.error('Failed to merge leads');
    }
  };

  const handleDelete = (lead) => {
    setSelectedLead(lead);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/leads/${selectedLead.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Duplicate lead deleted');
      setShowDeleteDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <HeaderNav title="Duplicate Leads" />

      <div className="max-w-7xl mx-auto p-4 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertTriangle className="mr-2 h-5 w-5 text-amber-600" />
              Duplicate Leads Management
            </CardTitle>
            <p className="text-sm text-slate-600">
              These leads have been flagged as duplicates. You can merge them with existing leads or delete them.
            </p>
          </CardHeader>
          <CardContent>
            {duplicateLeads.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-600">No duplicate leads found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lead #</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Projects</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Entry Method</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {duplicateLeads.map((lead) => (
                      <TableRow key={lead.id}>
                        <TableCell className="font-medium">{lead.lead_number}</TableCell>
                        <TableCell>{lead.customer_name}</TableCell>
                        <TableCell>{lead.mobile}</TableCell>
                        <TableCell className="text-sm">{lead.email || '-'}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {lead.projects?.map((p, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{p}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{lead.source}</TableCell>
                        <TableCell className="text-sm">{lead.entry_method || '-'}</TableCell>
                        <TableCell className="text-sm">
                          {lead.created_at && format(new Date(lead.created_at), 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMerge(lead)}
                            >
                              <GitMerge className="h-4 w-4 mr-1" />
                              Merge
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDelete(lead)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Merge Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge Duplicate Lead</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm font-medium">Duplicate Lead:</p>
              <p className="text-sm text-slate-600">{selectedLead?.lead_number} - {selectedLead?.customer_name}</p>
              <p className="text-sm text-slate-600">Mobile: {selectedLead?.mobile}</p>
              <p className="text-sm text-slate-600">Projects: {selectedLead?.projects?.join(', ')}</p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Merge into Lead ID or Lead Number:</label>
              <input
                type="text"
                value={targetLeadId}
                onChange={(e) => setTargetLeadId(e.target.value)}
                placeholder="Enter Lead # (e.g., L-0001) or Lead ID"
                className="w-full px-3 py-2 border rounded-md"
              />
              <p className="text-xs text-slate-500">
                The projects from this duplicate will be added to the target lead, and this duplicate will be deleted.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMergeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmMerge} className="bg-cyan-600 hover:bg-cyan-700">
              <GitMerge className="mr-2 h-4 w-4" />
              Merge Leads
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="mr-2 h-5 w-5" />
              Delete Duplicate Lead
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-700">Are you sure you want to delete this duplicate lead?</p>
            <div className="bg-slate-50 p-3 rounded-lg mt-3">
              <p className="text-sm font-medium">{selectedLead?.lead_number} - {selectedLead?.customer_name}</p>
              <p className="text-sm text-slate-600">Mobile: {selectedLead?.mobile}</p>
            </div>
            <p className="text-sm text-red-600 mt-3">This action cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DuplicateLeads;
